import React, { useState, ReactElement, useEffect } from 'react'
import { Button, message, Modal, Progress } from 'antd'
import { ProgressProps } from 'antd/lib/progress'
import { ONLambdaOneClickApi } from '@getgreenline/compliance'
import { CurrentCompanyStore } from '../../../../../../stores/CurrentCompanyStore'
import { ComplianceStore } from '../../ComplianceStore'
import { BLUE, GREEN } from '../../../../../../constants/Colors'
import { parseErrorMsg, sleep } from '../../../../../../utilities/helpers'
import { setRootAuthToken } from '../../../../../../utilities/environment'
import { getTokenV2 } from '../../../../../../utilities/auth'
import pRetry, { AbortError } from 'p-retry'
import { download } from '../../../../../../utilities/file'

interface Props {
  currentCompanyStore: CurrentCompanyStore
  complianceStore: ComplianceStore
}

export const OntarioOneClickReport = ({ currentCompanyStore, complianceStore }: Props) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  const [downloadProgress, setDownloadProgress] = useState<number>(0)
  const [downloadStatus, setDownloadStatus] = useState<ProgressProps['status']>('normal')
  const companyId = currentCompanyStore.company?.id
  if (!companyId) return null

  const onDownload = async () => {
    setIsModalOpen(true)
    // Enable refresh/navigation confirm prompt
    window.onbeforeunload = () => true

    try {
      const { month, year, locationIds } = complianceStore

      // use fresh token in case it's expired
      const authToken = await getTokenV2()
      if (!authToken) return

      setRootAuthToken(authToken)

      const fileName = await ONLambdaOneClickApi.createONComplianceReport(companyId, {
        month,
        year,
        locationIds,
      })

      const fileS3Url = await pRetry(
        () => ONLambdaOneClickApi.getONComplianceReportUrl(companyId, fileName),
        {
          retries: 25,
          onFailedAttempt: async (error: any) => {
            if (error?.response?.status !== 404) {
              throw new AbortError(error)
            }
            if (downloadProgress < 90) {
              // Increase and stay at 90% until response/error
              setDownloadProgress((p) => p + 5)
            }
            await sleep(4000)
          },
        },
      )

      // Remove refresh/navigation confirm prompt, placed before downloadFile as that counts as navigation
      window.onbeforeunload = null
      download(fileS3Url, fileName)

      setDownloadProgress(100)
      setDownloadStatus('success')
    } catch (e) {
      // Remove refresh/navigation confirm prompt
      window.onbeforeunload = null

      setDownloadStatus('exception')
      message.error(parseErrorMsg(e) || 'There was an error while downloading, please try again')
    } finally {
      // Close modal and reset progress
      setIsModalOpen(false)
      setDownloadProgress(0)
      setDownloadStatus('normal')
    }
  }

  return (
    <>
      <Button type='primary' onClick={onDownload}>
        Download Integrated Report
      </Button>
      <Modal visible={isModalOpen} closable={false} footer={null}>
        <div className='d-flex justify-content-center mb-4'>
          <Progress
            type='circle'
            percent={downloadProgress}
            strokeWidth={10}
            width={230}
            strokeColor={{
              '0%': GREEN.green600,
              '100%': BLUE.defaultBlue,
            }}
            role='progressbar'
            aria-valuenow={downloadProgress}
            aria-valuemin='0'
            aria-valuemax='100'
            status={downloadStatus}
          />
        </div>
        <div>We are generating your report. This may take a moment, please stand by...</div>
      </Modal>
    </>
  )
}
